import clsx from 'clsx';
import React, { useMemo, useRef, useState, useEffect } from 'react';

import Checkbox from '../Checkbox/Checkbox';
import bottomAngleIcon from '../../../assets/bottomAngleIcon.svg';

import styles from './MultiSelectDropdown.module.scss';
import { isEmpty } from '../../../utils/utils';
import { useOutsideClick } from '../../../utils/constants/hooks';
import { CHIP_MEASURE_ATTR, useAutoFitChips } from '../Hooks/useAutoFitChips';

type Props = {
    placeholder?: string;
    options: { id: string; label: string }[];
    selectedOptions: string[];
    onChange: (selectedOptions: string[]) => void;
    disabled?: boolean;
    WrapperStyle?: React.CSSProperties;
    hasError?: boolean;
    errorMessage?: string;
    showFloatingLabel?: boolean;
};

const MultiSelectDropdown: React.FC<Props> = ({
    placeholder,
    options,
    selectedOptions,
    onChange,
    disabled = false,
    hasError,
    errorMessage,
    WrapperStyle,
    showFloatingLabel = true,
}) => {
    const [showList, setShowList] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [filteredOptions, setFilteredOptions] = useState(options);

    useEffect(() => {
        if (searchText.trim() === '') {
            setFilteredOptions(options);
        } else {
            setFilteredOptions(options.filter((opt) => opt.label.toLowerCase().includes(searchText.toLowerCase())));
        }
    }, [searchText, options]);

    const handleSelectAll = () => {
        const visibleOptionIds = filteredOptions.map((opt) => opt.id);
        const selectedSet = new Set(selectedOptions);
        const allVisibleSelected = visibleOptionIds.every((id) => selectedSet.has(id));

        if (!allVisibleSelected) {
            const newSelection = Array.from(new Set([...selectedOptions, ...visibleOptionIds]));
            onChange(newSelection);
        }
    };

    const handleClearAll = () => {
        const visibleOptionIds = filteredOptions.map((opt) => opt.id);
        const newSelection = selectedOptions.filter((id) => !visibleOptionIds.includes(id));
        onChange(newSelection);
    };

    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useOutsideClick(dropdownRef, () => {
        closeMenu();
    });

    const toggleMenu = () => {
        if (disabled) return;
        setShowList(!showList);
        if (!showList) inputRef.current?.focus();
    };

    const closeMenu = () => {
        setShowList(false);
    };

    const { chipRowRef, visibleChipCount } = useAutoFitChips(true, selectedOptions?.length ?? 0);

    const renderSelectedOptions = useMemo(() => {
        if (!selectedOptions?.length) return <div className={styles.EmptyDiv} />;
        const selectedLabels = selectedOptions.map((id) => options.find((option) => option.id === id)?.label ?? id);
        const visible = visibleChipCount ? selectedLabels.slice(0, visibleChipCount) : selectedLabels;
        const hiddenCount = selectedLabels.length - visible.length;
        return (
            <>
                <div className={styles.SelectedOptionsWrap} ref={chipRowRef}>
                    {visible.map((label, index) => (
                        <div
                            key={`selected-option-${label}-${index}`}
                            className={styles.SelectedOption}
                            {...{ [CHIP_MEASURE_ATTR]: true }}
                            style={index === visible.length - 1 ? { flexShrink: 1, minWidth: '4ch' } : undefined}
                        >
                            <div className={styles.SelectedOptionLabel} title={label}>
                                {label}
                            </div>
                        </div>
                    ))}
                </div>
                {hiddenCount > 0 && (
                    <span className={styles.MoreOptionText} title={selectedLabels.slice(visible.length).join(', ')}>
                        + {hiddenCount} more
                    </span>
                )}
            </>
        );
    }, [selectedOptions, options, visibleChipCount, chipRowRef]);

    const onMultipleOptionClick = (option: { id: string; label: string }, checked: boolean) => {
        const newSelectedOptions = checked
            ? [...selectedOptions, option.id]
            : selectedOptions.filter((selectedOption) => selectedOption !== option.id);

        onChange(newSelectedOptions);
    };

    const shouldShowLabel = showFloatingLabel || !selectedOptions?.length;

    return (
        <div
            style={WrapperStyle}
            className={clsx(styles.MultiSelectDropdown, {
                [styles.Disabled]: disabled,
            })}
            onClick={toggleMenu}
            onMouseDown={(e) => {
                e.preventDefault();
            }}
            ref={dropdownRef}
        >
            <input
                className={styles.HiddenInput}
                style={{
                    height: '44px',
                }}
                type="text"
                name="hidden-input"
                id="hidden-input"
                ref={inputRef}
                onBlur={() => {
                    // Only close the menu if the blur event is not caused by clicking inside the dropdown
                    setTimeout(() => {
                        if (!dropdownRef.current?.contains(document.activeElement)) {
                            closeMenu();
                        }
                    }, 0);
                }}
                value={''}
                onChange={() => {}}
            />
            <div
                className={clsx(styles.InputBox, {
                    [styles.OptionsSelected]: selectedOptions?.length > 0,
                    [styles.Error]: hasError && !!errorMessage,
                })}
            >
                {shouldShowLabel && <label className={styles.DropdownLabel}>{placeholder}</label>}
                {renderSelectedOptions}
                {!disabled && (
                    <img
                        className={clsx(styles.DropdownIcon, {
                            [styles.DropdownIconInverted]: showList,
                        })}
                        src={bottomAngleIcon}
                        alt="Caret Down"
                    />
                )}
            </div>
            {showList && (
                <div className={styles.OptionsMenu}>
                    <input
                        type="text"
                        placeholder="Search"
                        className={styles.SearchInput}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    />

                    {filteredOptions.length > 0 && (
                        <div className={styles.SelectControlsCheckboxRow}>
                            <Checkbox
                                label="Select All"
                                checked={filteredOptions.every((opt) => selectedOptions.includes(opt.id))}
                                onChange={() => handleSelectAll()}
                                isMultiSelect
                                customStyles={{ padding: '14px 10px' }}
                            />
                            <button
                                type="button"
                                className={styles.ClearBtn}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearAll();
                                }}
                            >
                                Clear All
                            </button>
                        </div>
                    )}

                    <div className={styles.OptionsList}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <Checkbox
                                    key={`multi-select-option-${option.id}`}
                                    label={option.label}
                                    checked={selectedOptions.includes(option.id)}
                                    onChange={(checked) => onMultipleOptionClick(option, checked)}
                                    isMultiSelect
                                    customStyles={{ padding: '14px 10px' }}
                                />
                            ))
                        ) : (
                            <div className={styles.NoData}>No options found</div>
                        )}
                    </div>
                </div>
            )}

            {!isEmpty(errorMessage) && hasError && (
                <div
                    className={clsx(styles.ErrorMsg, {
                        [styles.HideErrorMsg]: !hasError,
                    })}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
